namespace $.$$ {

	export class $shm_date_view extends $.$shm_date_view {

		input_mask( val: string ) {
			return val.length > 8 ? '__.__.____ __:__' : '__.__.____'
		}

		date_day( date: string ) {
			return date.slice( 0, 10 )
		}

		date_time( date: string ) {
			const match = date.match( /^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})/ )
			if( !match ) return ''
			return `${ match[1] }:${ match[2] }`
		}

		date_fit( date: string ) {
			const time = this.date_time( date )
			const match = this.date_day( date ).match( /^(\d{4})-(\d{2})-(\d{2})$/ )
			if( !match ) return date

			const year = Number( match[1] )
			const month = Math.min( 12, Math.max( 1, Number( match[2] ) ) )
			const day_max = new Date( year, month, 0 ).getDate()
			const day = Math.min( day_max, Math.max( 1, Number( match[3] ) ) )

			const result = [
				String( year ).padStart( 4, '0' ),
				String( month ).padStart( 2, '0' ),
				String( day ).padStart( 2, '0' ),
			].join( '-' )
			return time ? `${ result } ${ time }` : result
		}

		date_clamp( date: string ) {
			if( !date ) return date
			if( !/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2})?$/.test( date ) ) return date
			date = this.date_fit( date )
			const min = this.value_min()
			const max = this.value_max()
			const day = this.date_day( date )
			if( min && day < min ) return min
			if( max && day > max ) return max
			return date
		}

		date_valid( date: string ) {
			if( !/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2})?$/.test( date ) ) return false
			const moment = $mol_try( ()=> new $mol_time_moment( date ) )
			const format = this.date_time( date ) ? 'YYYY-MM-DD hh:mm' : 'YYYY-MM-DD'
			return !( moment instanceof Error ) && moment.toString( format ) === date
		}

		value( next?: string ) {
			if( next === undefined ) return super.value()
			return super.value( this.date_clamp( next ) )
		}

		input_value( next?: string ) {
			if( next === undefined ) return this.$.$shm_date.text( this.value() )
			const iso = this.$.$shm_date.iso( next )
			const fit = this.date_fit( iso )
			if( this.date_valid( fit ) && this.date_clamp( fit ) === fit ) this.value( fit )
			return next
		}

		input_commit( next?: Event ) {
			if( next === undefined ) return null

			const iso = this.$.$shm_date.iso( this.value_changed() )
			const fit = this.date_fit( iso )
			const value = this.date_valid( fit ) ? this.date_clamp( fit ) : this.value()
			this.value( value )
			this.Input().value_changed( this.$.$shm_date.text( this.value() ) )

			return next
		}

		@ $mol_mem
		month_moment( next?: $mol_time_moment ) {
			if( next ) return next

			const value = this.value() || this.$.$shm_date.iso( this.value_changed().replace( /\D+$/, '' ) )
			let moment = $mol_try( ()=> new $mol_time_moment( value ).mask( '0000-00' ) )
			if( moment instanceof Error || !moment.year ) return new $mol_time_moment().mask( '0000-00' )

			if( moment.month === undefined ) moment = moment.merge({ month: 0 })
			return moment
		}

		trigger_content() {
			const value = this.value()
			return [ value ? this.$.$shm_date.text( value ) : this.Icon() ]
		}

		day_selected( day: string ) {
			return this.date_day( this.value() ) === day
		}

		day_enabled( day: string ) {
			return this.date_clamp( day ) === day
		}

		date_text( date: Date ) {
			const year = date.getFullYear()
			const month = String( date.getMonth() + 1 ).padStart( 2, '0' )
			const day = String( date.getDate() ).padStart( 2, '0' )
			return `${ year }-${ month }-${ day }`
		}

		month_available( moment: $mol_time_moment ) {
			if( !moment.year ) return true

			const month = moment.month ?? 0
			const from = this.date_text( new Date( moment.year, month, 1 ) )
			const to = this.date_text( new Date( moment.year, month + 1, 0 ) )
			const min = this.value_min()
			const max = this.value_max()

			if( min && to < min ) return false
			if( max && from > max ) return false
			return true
		}

		prev_enabled() {
			return this.month_available( this.month_moment().shift({ month: -1 }) )
		}

		next_enabled() {
			return this.month_available( this.month_moment().shift({ month: 1 }) )
		}

		prev( next?: Event ) {
			if( next === undefined ) return null
			if( !this.prev_enabled() ) return next
			this.month_moment( this.month_moment().shift({ month: -1 }) )
			return next
		}

		next( next?: Event ) {
			if( next === undefined ) return null
			if( !this.next_enabled() ) return next
			this.month_moment( this.month_moment().shift({ month: 1 }) )
			return next
		}

		override day_click( day: string ) {
			if( !this.day_enabled( day ) ) return null

			const time = this.date_time( this.value() )
			const moment = new $mol_time_moment( day )
			this.value_moment( this.value_moment()?.merge( moment ) ?? moment )
			if( time ) this.value( `${ day } ${ time }` )
			this.Input().value_changed( this.$.$shm_date.text( this.value() ) )
			this.showed( false )
		}

		override today_click() {
			this.value( new $mol_time_moment().toString( 'YYYY-MM-DD' ) )
			this.Input().value_changed( this.$.$shm_date.text( this.value() ) )
		}

		override clear() {
			super.clear()
			this.Input().value_changed( '' )
		}

	}

	export class $shm_date_input extends $.$shm_date_input {

		date_normalize( val: string ) {
			const mask = this.mask( val )
			const chars = [ ... val ]
			const positional = chars.some( letter => letter === '_' || !this.allow().includes( letter ) )
				|| chars.length >= mask.length

			if( !positional ) {
				const letters = chars.filter( letter => this.allow().includes( letter ) ).reverse()
				return mask.replace( /_/gu, ()=> letters.pop() ?? '_' )
			}

			let cursor = 0
			return [ ... mask ].map( letter => {
				if( letter !== '_' ) return letter
				while( cursor < chars.length && !this.allow().includes( chars[ cursor ] ) && chars[ cursor ] !== '_' ) ++ cursor
				const next = chars[ cursor++ ]
				return this.allow().includes( next ) ? next : '_'
			} ).join( '' )
		}

		@ $mol_mem
		override value_changed( next?: string ) {
			if( next !== undefined ) {
				next = this.date_normalize( next )
				if( /[0-9]/.test( next ) ) {
					if( next.includes( '_' ) ) return next
				} else {
					next = ''
				}
			}

			return this.date_normalize( this.value( next ) )
		}

		@ $mol_mem
		override selection( [ from, to ] = [ 0, 0 ] ): number[] {
			const prev = $mol_wire_probe( ()=> this.selection() )
			if( !prev ) return [ from, to ]

			if( from !== to ) return [ from, to ]

			const allow = this.allow()
			const value = this.value_changed()
			const filtered = [ ... value ].filter( letter => allow.includes( letter ) ).join( '' )
			const mask = this.mask( filtered )

			if( ( prev?.[0] ?? 0 ) >= from ) return [ from, to ]

			const lastAllow = (
				value.length - [ ... value ].reverse().findIndex( letter => allow.includes( letter ) )
			) % ( value.length + 1 )

			if( lastAllow < from ) from = to = lastAllow

			while( mask[ from ] && mask[ from ] !== '_' ) {
				++ from
				++ to
			}

			return [ from, to ]
		}

		override event_change( next?: Event ) {
			const el = this.dom_node() as HTMLInputElement
			const prev = this.value_changed()
			const caret = el.selectionStart
			const input = next as InputEvent
			const [ selection_from, selection_to ] = this.selection()
			let caret_next = null as number | null
			let caret_skip_separators = false
			if(
				next
				&& caret !== null
				&& input.inputType === 'insertText'
				&& selection_to > selection_from
				&& input.data
				// Whole-field replacement already contains the authoritative new text.
				// selectionchange can still describe an earlier partial selection.
				&& input.data !== el.value
			) {
				const chars = [ ... prev ]
				const data = [ ... input.data ].filter( letter => this.allow().includes( letter ) )
				let data_index = 0
				let replaced_at = null as number | null
				let caret_target = selection_from

				for( let index = selection_from; index < selection_to; ++ index ) {
					if( !/[0-9_]/.test( chars[ index ] ) ) continue

					const letter = data[ data_index++ ]
					chars[ index ] = letter ?? '_'
					replaced_at ??= index
					if( letter ) caret_target = index + 1
				}

				el.value = chars.join( '' )
				caret_next = data.length ? caret_target : replaced_at ?? selection_from
				caret_skip_separators = true
			} else if( next && caret !== null && input.inputType?.startsWith( 'delete' ) && el.value.length < prev.length ) {
				const chars = [ ... prev ]
				let from = 0
				let to_prev = prev.length
				let to_next = el.value.length

				while( from < to_next && prev[ from ] === el.value[ from ] ) ++ from
				while( to_prev > from && to_next > from && prev[ to_prev - 1 ] === el.value[ to_next - 1 ] ) {
					-- to_prev
					-- to_next
				}

				let replaced = false
				let replaced_at = null as number | null
				for( let index = from; index < to_prev; ++ index ) {
					if( /[0-9_]/.test( chars[ index ] ) ) {
						chars[ index ] = '_'
						replaced = true
						replaced_at ??= index
					}
				}

				if( !replaced ) {
					let index = input.inputType === 'deleteContentBackward' ? from - 1 : from
					while( chars[ index ] && !/[0-9_]/.test( chars[ index ] ) ) {
						index += input.inputType === 'deleteContentBackward' ? -1 : 1
					}
					if( chars[ index ] ) {
						chars[ index ] = '_'
						replaced_at = index
					}
				}

				el.value = chars.join( '' )
				caret_next = replaced_at ?? from
			} else if( next && caret !== null && el.value.length === prev.length + 1 ) {
				const inserted = el.value.slice( Math.max( 0, caret - 1 ), caret )
				if( /^[0-9]$/.test( inserted ) ) {
					const chars = [ ... prev ]
					const insert_at = Math.max( 0, caret - 1 )
					let target = insert_at

					if( !/[0-9_]/.test( chars[ target ] ?? '' ) ) {
						while( chars[ target ] && chars[ target ] !== '_' ) ++ target
					}

					if( chars[ target ] === '_' ) {
						chars[ target ] = inserted
						el.value = chars.join( '' )
						caret_next = target + 1
						caret_skip_separators = true
					}
				}
			}
			const result = super.event_change( next )
			if( caret_next !== null && el === this.$.$mol_dom_context.document.activeElement ) {
				const mask = this.mask( [ ... el.value ].filter( letter => this.allow().includes( letter ) ).join( '' ) )
				while( caret_skip_separators && mask[ caret_next ] && mask[ caret_next ] !== '_' ) ++ caret_next
				el.selectionStart = caret_next
				el.selectionEnd = caret_next
				this.selection([ caret_next, caret_next ])
			}
			return result
		}

	}

	export class $shm_date_calendar extends $.$shm_date_calendar {

		override day_theme( day: string ) {
			return this.day_today( day ) ? '$mol_theme_current' : null
		}

	}

}
